import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RecentVerifications = ({ verifications, onSelectVerification, onClearHistory }) => {
  if (!verifications || verifications?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-soft border border-border p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="History" size={24} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            No Recent Verifications
          </h3>
          <p className="text-sm text-muted-foreground">
            Your verification history will appear here after you check your first claim.
          </p>
        </div>
      </div>
    );
  }

  const getCredibilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getCredibilityBg = (score) => {
    if (score >= 80) return 'bg-success/10';
    if (score >= 60) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const truncateText = (text, maxLength = 100) => {
    if (text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-card rounded-xl shadow-soft border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-accent rounded-lg flex items-center justify-center">
            <Icon name="History" size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Recent Verifications
            </h3>
            <p className="text-sm text-muted-foreground">
              {verifications?.length} verification{verifications?.length !== 1 ? 's' : ''} in history
            </p>
          </div>
        </div>
        
        {verifications?.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearHistory}
            iconName="Trash2"
            iconSize={14}
            className="text-muted-foreground hover:text-error"
          >
            Clear
          </Button>
        )}
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {verifications?.map((verification, index) => (
          <div
            key={index}
            className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors duration-200 cursor-pointer group"
            onClick={() => onSelectVerification(verification)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                  {truncateText(verification?.originalClaim)}
                </p>
                <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Icon name="Calendar" size={12} />
                    <span>{verification?.timestamp}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} />
                    <span>{verification?.timeAgo}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name={verification?.type === 'url' ? 'Link' : 'Type'} size={12} />
                    <span className="capitalize">{verification?.type}</span>
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 ml-4">
                <div className={`px-2 py-1 rounded-full ${getCredibilityBg(verification?.credibilityScore)}`}>
                  <span className={`text-xs font-medium ${getCredibilityColor(verification?.credibilityScore)}`}>
                    {verification?.credibilityScore}%
                  </span>
                </div>
                <Icon 
                  name="ChevronRight" 
                  size={16} 
                  color="var(--color-muted-foreground)" 
                  className="group-hover:translate-x-1 transition-transform duration-200"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={verification?.credibilityScore >= 80 ? 'CheckCircle' : verification?.credibilityScore >= 60 ? 'AlertTriangle' : 'XCircle'} 
                  size={14} 
                  color={verification?.credibilityScore >= 80 ? 'var(--color-success)' : verification?.credibilityScore >= 60 ? 'var(--color-warning)' : 'var(--color-error)'} 
                />
                <span className="text-xs text-muted-foreground">
                  {verification?.evidenceCount} source{verification?.evidenceCount !== 1 ? 's' : ''} verified
                </span>
              </div>
              
              <div className="text-xs text-muted-foreground">
                Click to view details
              </div>
            </div>
          </div>
        ))}
      </div>
      {verifications?.length > 5 && (
        <div className="mt-4 pt-4 border-t border-border text-center">
          <Button
            variant="ghost"
            size="sm"
            iconName="MoreHorizontal"
            iconPosition="left"
          >
            View All History
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentVerifications;